import { supabase } from "@/lib/supabase";
import { aiService } from "./aiService";

export interface PersonalizedQuote {
  id: string;
  text: string;
  author: string;
  expertise: string;
  topic: string;
  generatedAt: Date;
}

export interface CognitiveItem {
  id: string;
  question: string;
  answer: string;
  type: "flashcard" | "did_you_know" | "faq" | "quiz";
  topic: string;
  source?: string;
  generatedAt: Date;
}

interface ThreadSummary {
  title: string;
  topic: string;
  keyThemes: string[];
  recentContent: string;
}

const CACHE_KEY_QUOTES = "personalized_quotes_cache";
const CACHE_KEY_COGNITIVE = "cognitive_items_cache";
const CACHE_DURATION_MS = 4 * 60 * 60 * 1000;

class PersonalizationService {
  private async getRecentThreadsWithContent(userId: string, limit: number = 10): Promise<ThreadSummary[]> {
    try {
      const { data: threads, error } = await supabase
        .from("chat-threads")
        .select("id, title, topic, messages, updatedAt")
        .eq("userId", userId)
        .order("updatedAt", { ascending: false })
        .limit(limit);

      if (error || !threads) {
        console.error("Error fetching threads:", error);
        return [];
      }

      return threads.map((thread: any) => {
        const messages = Array.isArray(thread.messages) ? thread.messages : [];
        const recentMessages = messages.slice(-5);
        
        const contentPieces: string[] = [];
        recentMessages.forEach((msg: any) => {
          if (msg.content && typeof msg.content === "string") {
            contentPieces.push(msg.content.substring(0, 300));
          }
        });

        const keyThemes = this.extractKeyThemes(contentPieces.join(" "));

        return {
          title: thread.title || "Untitled",
          topic: thread.topic || "",
          keyThemes,
          recentContent: contentPieces.join("\n---\n").substring(0, 1000),
        };
      });
    } catch (error) {
      console.error("Error in getRecentThreadsWithContent:", error);
      return [];
    }
  }

  private extractKeyThemes(content: string): string[] {
    const themeKeywords = [
      "productivity", "success", "growth", "learning", "leadership", "innovation",
      "creativity", "motivation", "resilience", "mindset", "focus", "goals",
      "business", "career", "health", "wellness", "relationships", "money",
      "investing", "technology", "science", "philosophy", "spirituality",
      "happiness", "purpose", "meaning", "change", "improvement", "strategy",
      "problem", "solution", "challenge", "opportunity", "skill", "knowledge",
      "research", "analysis", "decision", "planning", "execution", "progress",
      "AI", "machine learning", "data", "coding", "programming", "design",
      "marketing", "sales", "finance", "psychology", "fitness", "nutrition"
    ];
    
    const lowerContent = content.toLowerCase();
    return themeKeywords.filter(keyword => lowerContent.includes(keyword.toLowerCase()));
  }

  private isClientSide(): boolean {
    return typeof window !== "undefined" && typeof localStorage !== "undefined";
  }

  private getCachedData<T>(cacheKey: string): { data: T; timestamp: number } | null {
    if (!this.isClientSide()) return null;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_DURATION_MS) {
          return parsed;
        }
      }
    } catch (error) {
      console.error("Cache read error:", error);
    }
    return null;
  }

  private setCachedData<T>(cacheKey: string, data: T): void {
    if (!this.isClientSide()) return;
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error("Cache write error:", error);
    }
  }

  private safeParseJSON<T>(response: string): T | null {
    try {
      let jsonStr = response.trim();
      const jsonArrayMatch = jsonStr.match(/\[[\s\S]*?\]/);
      if (jsonArrayMatch) {
        jsonStr = jsonArrayMatch[0];
      }
      jsonStr = jsonStr.replace(/```json\s*/gi, "").replace(/```\s*/g, "").trim();
      const parsed = JSON.parse(jsonStr);
      if (!Array.isArray(parsed)) {
        console.warn("AI response was not an array:", typeof parsed);
        return null;
      }
      return parsed as T;
    } catch (error) {
      console.warn("Failed to parse AI response as JSON:", error);
      return null;
    }
  }

  async generatePersonalizedQuotes(userId: string, count: number = 5): Promise<PersonalizedQuote[]> {
    const cacheKey = `${CACHE_KEY_QUOTES}_${userId}`;
    const cached = this.getCachedData<PersonalizedQuote[]>(cacheKey);
    if (cached) {
      return cached.data;
    }

    try {
      const threads = await this.getRecentThreadsWithContent(userId, 10);
      
      if (threads.length === 0) {
        return this.getDefaultQuotes();
      }

      const topicsAndThemes = threads
        .flatMap(t => [t.topic, t.title, ...t.keyThemes])
        .filter(Boolean)
        .slice(0, 20);

      const contentSummary = threads
        .slice(0, 5)
        .map(t => `Topic: ${t.topic || t.title}\nContent: ${t.recentContent.substring(0, 200)}`)
        .join("\n\n");

      const prompt = `Based on the user's recent research and interests, generate ${count} inspiring and relevant quotes.

User's recent topics and interests:
${topicsAndThemes.join(", ")}

Sample content from their research:
${contentSummary}

Generate ${count} unique, inspiring quotes that would resonate with someone researching these topics. Each quote should feel personally relevant to their interests.

Return ONLY a valid JSON array with this exact format (no markdown, no explanation):
[
  {"text": "quote text here", "author": "Author Name", "expertise": "field of expertise", "topic": "main topic"}
]`;

      const response = await aiService.complete(prompt, { maxTokens: 800, temperature: 0.8, app: "other" });
      
      const parsed = this.safeParseJSON<any[]>(response);
      if (!parsed || parsed.length === 0) {
        console.warn("AI quote parsing failed, using defaults");
        return this.getDefaultQuotes();
      }
      
      const quotes: PersonalizedQuote[] = parsed.slice(0, count).map((q: any, idx: number) => ({
        id: `ai-quote-${Date.now()}-${idx}`,
        text: typeof q.text === "string" ? q.text : "Stay curious, stay learning.",
        author: typeof q.author === "string" ? q.author : "Unknown",
        expertise: typeof q.expertise === "string" ? q.expertise : "Wisdom",
        topic: typeof q.topic === "string" ? q.topic : "General",
        generatedAt: new Date(),
      }));

      this.setCachedData(cacheKey, quotes);
      return quotes;
    } catch (error) {
      console.error("Error generating personalized quotes:", error);
      return this.getDefaultQuotes();
    }
  }

  async generateCognitiveItems(userId: string, count: number = 8): Promise<CognitiveItem[]> {
    const cacheKey = `${CACHE_KEY_COGNITIVE}_${userId}`;
    const cached = this.getCachedData<CognitiveItem[]>(cacheKey);
    if (cached) {
      return cached.data;
    }

    try {
      const threads = await this.getRecentThreadsWithContent(userId, 10);
      
      if (threads.length === 0) {
        return this.getDefaultCognitiveItems();
      }

      const contentForAI = threads
        .slice(0, 6)
        .map(t => `Topic: ${t.topic || t.title}\nKey themes: ${t.keyThemes.join(", ")}\nContent snippet: ${t.recentContent.substring(0, 300)}`)
        .join("\n\n---\n\n");

      const prompt = `Based on the user's recent research and conversations, create personalized learning content.

User's recent research:
${contentForAI}

Generate ${count} learning items based on their actual interests and research. Create a mix of:
- 2 flashcards (key concepts to remember)
- 2 "did you know" facts (interesting insights)
- 2 FAQ items (common questions they might have)
- 2 quiz questions (to test understanding)

Return ONLY a valid JSON array with this exact format (no markdown):
[
  {"question": "front/prompt text", "answer": "back/response text", "type": "flashcard|did_you_know|faq|quiz", "topic": "topic name"}
]`;

      const response = await aiService.complete(prompt, { maxTokens: 1200, temperature: 0.7, app: "other" });
      
      const parsed = this.safeParseJSON<any[]>(response);
      if (!parsed || parsed.length === 0) {
        console.warn("AI cognitive items parsing failed, using defaults");
        return this.getDefaultCognitiveItems();
      }
      
      const items: CognitiveItem[] = parsed.slice(0, count).map((item: any, idx: number) => ({
        id: `ai-cognitive-${Date.now()}-${idx}`,
        question: typeof item.question === "string" ? item.question : "What did you learn recently?",
        answer: typeof item.answer === "string" ? item.answer : "Keep exploring and growing!",
        type: this.normalizeType(item.type || ""),
        topic: typeof item.topic === "string" ? item.topic : "Your Research",
        source: "Your recent conversations",
        generatedAt: new Date(),
      }));

      this.setCachedData(cacheKey, items);
      return items;
    } catch (error) {
      console.error("Error generating cognitive items:", error);
      return this.getDefaultCognitiveItems();
    }
  }

  private normalizeType(type: string): CognitiveItem["type"] {
    const normalized = type?.toLowerCase()?.replace(/_/g, "_");
    if (["flashcard", "flashcards"].includes(normalized)) return "flashcard";
    if (["did_you_know", "didyouknow", "fact"].includes(normalized)) return "did_you_know";
    if (["faq", "question"].includes(normalized)) return "faq";
    if (["quiz", "test"].includes(normalized)) return "quiz";
    return "flashcard";
  }

  clearCache(userId: string): void {
    if (!this.isClientSide()) return;
    try {
      localStorage.removeItem(`${CACHE_KEY_QUOTES}_${userId}`);
      localStorage.removeItem(`${CACHE_KEY_COGNITIVE}_${userId}`);
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  }

  private getDefaultQuotes(): PersonalizedQuote[] {
    return [
      { id: "default-1", text: "The only way to do great work is to love what you do.", author: "Steve Jobs", expertise: "Innovation", topic: "passion", generatedAt: new Date() },
      { id: "default-2", text: "Knowledge is power. Information is liberating.", author: "Kofi Annan", expertise: "Leadership", topic: "knowledge", generatedAt: new Date() },
      { id: "default-3", text: "The mind is everything. What you think you become.", author: "Buddha", expertise: "Mindfulness", topic: "mindset", generatedAt: new Date() },
      { id: "default-4", text: "In the middle of difficulty lies opportunity.", author: "Albert Einstein", expertise: "Science", topic: "resilience", generatedAt: new Date() },
      { id: "default-5", text: "Stay hungry, stay foolish.", author: "Steve Jobs", expertise: "Innovation", topic: "curiosity", generatedAt: new Date() },
    ];
  }

  private getDefaultCognitiveItems(): CognitiveItem[] {
    return [
      { id: "default-c1", question: "What is spaced repetition?", answer: "A learning technique that involves reviewing information at increasing intervals to optimize memory retention.", type: "flashcard", topic: "Learning", generatedAt: new Date() },
      { id: "default-c2", question: "Reading just 20 minutes a day exposes you to 1.8 million words per year!", answer: "This massive vocabulary exposure is linked to improved cognitive function and memory.", type: "did_you_know", topic: "Reading", generatedAt: new Date() },
      { id: "default-c3", question: "How does memory consolidation work during sleep?", answer: "During sleep, the brain replays and strengthens neural pathways formed during learning.", type: "faq", topic: "Memory", generatedAt: new Date() },
      { id: "default-c4", question: "Which learning technique uses increasing time intervals between reviews?", answer: "Spaced Repetition", type: "quiz", topic: "Learning", generatedAt: new Date() },
    ];
  }
}

export const personalizationService = new PersonalizationService();
